import { z } from 'zod';
import { APP_ID_PATTERN_SOURCE } from './slug.js';

/** Extension inferred from the uploaded file; must match package kind expectations. */
export const PACKAGE_EXTENSIONS = ['msix', 'appx', 'msi', 'exe', 'zip'] as const;
export const packageExtensionSchema = z.enum(PACKAGE_EXTENSIONS);
export type PackageExtension = z.infer<typeof packageExtensionSchema>;

const sha256Schema = z
  .string()
  .regex(/^[a-f0-9]{64}$/i, 'sha256 must be 64 hex characters')
  .transform((v) => v.toLowerCase());

const MSIX_EXTENSIONS = new Set(['msix', 'appx']);
const EXE_EXTENSIONS = new Set(['exe']);
const MSI_EXTENSIONS = new Set(['msi']);
const ZIP_EXTENSIONS = new Set(['zip']);

export const packageKindsForExtension = (ext: PackageExtension): string[] => {
  if (MSIX_EXTENSIONS.has(ext)) return ['msix', 'appx'];
  if (EXE_EXTENSIONS.has(ext)) return ['exe'];
  if (MSI_EXTENSIONS.has(ext)) return ['msi'];
  if (ZIP_EXTENSIONS.has(ext)) return ['zip'];
  return [];
};

/**
 * A file name is a single path segment with a safe extension. It is validated
 * before any R2 key is built from it, so uploads can never escape their prefix.
 */
export const fileNameSchema = z
  .string()
  .min(1)
  .max(260)
  .refine((v) => !v.includes('/') && !v.includes('\\'), 'file name must not contain path separators')
  .refine((v) => !v.includes('..'), 'file name must not contain ".."')
  .refine((v) => ![...v].some((char) => char.charCodeAt(0) < 32), 'file name must not contain control characters');

const installerSectionSchema = z.object({
  /** Arguments that make the installer run unattended. */
  silent: z.array(z.string().min(1)).min(1),
  /** Arguments used when upgrading an already installed copy. */
  upgrade: z.array(z.string().min(1)).optional(),
  /** Arguments used by `uninstall` (reserved for a future release). */
  uninstall: z.array(z.string().min(1)).optional(),
});

export const manifestReleaseSchema = z
  .object({
    version: z
      .string()
      .min(1)
      .max(64)
      .regex(/^[0-9A-Za-z][0-9A-Za-z.+-]*$/, 'version must be a semver-like token'),
    platform: z.literal('windows'),
    arch: z.array(z.string().min(1)).min(1),
    package: z.object({
      kind: z.enum(['msix', 'appx', 'msi', 'exe', 'zip']),
      file: fileNameSchema,
      // Optional: committed in the manifest for reproducible local builds.
      sha256: sha256Schema.optional(),
      // Present for winget-origin packages that are hosted by the publisher.
      url: z
        .string()
        .url()
        .refine((u) => u.startsWith('https://'), 'package url must use https')
        .optional(),
    }),
    scope: z.enum(['per_user', 'machine', 'portable']),
    installer: installerSectionSchema.optional(),
    minOs: z.string().regex(/^\d+\.\d+\.\d+$/).optional(),
    changelog: z.record(z.string().min(2).max(5), z.string().min(1).max(20000)).optional(),
  })
  .superRefine((release, ctx) => {
    const kind = release.package.kind;
    const ext = release.package.file.split('.').pop()?.toLowerCase();
    const kindOfFile = (ext ?? '') as PackageExtension;
    if (!packageKindsForExtension(kindOfFile).includes(kind)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['package', 'file'],
        message: `file extension does not match package kind "${kind}"`,
      });
    }
    if (kind === 'exe' || kind === 'msi') {
      if (!release.installer || release.installer.silent.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['installer'],
          message: 'exe/msi releases require silentArgs for unattended updates',
        });
      }
    }
    if (kind === 'msix' || kind === 'appx') {
      if (release.scope === 'portable') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['scope'],
          message: 'msix packages cannot be portable',
        });
      }
    }
  });

export const manifestSchema = z.object({
  schemaVersion: z.literal(1),
  appId: z
    .string()
    .min(3)
    .max(128)
    .regex(new RegExp(APP_ID_PATTERN_SOURCE), 'appId must be "developer-slug/app-slug"'),
  name: z.string().min(1).max(120),
  developer: z.object({
    name: z.string().min(1).max(120),
    email: z.string().email().optional(),
  }),
  category: z.enum([
    'audio-video',
    'business',
    'communication',
    'development',
    'education',
    'games',
    'graphics',
    'health',
    'news',
    'productivity',
    'security',
    'system',
    'utilities',
  ]),
  license: z.string().min(1).max(64),
  homepage: z
    .string()
    .url()
    .refine((u) => u.startsWith('https://'), 'homepage must use https')
    .optional(),
  summary: z.record(z.string().min(2).max(5), z.string().min(1).max(200)),
  description: z.record(z.string().min(2).max(5), z.string().min(1).max(20000)),
  releases: z.array(manifestReleaseSchema).min(1).max(50),
}).superRefine((value, ctx) => {
  for (const field of ['summary', 'description'] as const) {
    if (!value[field].en || value[field].en.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [field, 'en'],
        message: 'English ("en") text is required',
      });
    }
  }
});

export type Manifest = z.infer<typeof manifestSchema>;
export type ManifestRelease = z.infer<typeof manifestReleaseSchema>;

export interface ManifestIssue {
  path: string;
  message: string;
}

/** Parse a manifest, returning either the value or a flat list of path issues. */
export function parseManifest(
  input: unknown,
): { ok: true; manifest: Manifest } | { ok: false; issues: ManifestIssue[] } {
  const parsed = manifestSchema.safeParse(input);
  if (parsed.success) return { ok: true, manifest: parsed.data };
  return {
    ok: false,
    issues: parsed.error.issues.map((issue) => ({
      path: issue.path.join('.'),
      message: issue.message,
    })),
  };
}

/** Appwin manifest v1 constants exposed to clients for version gating. */
export const MANIFEST_SCHEMA_VERSION = 1 as const;
